// v2 seagull — a soaring gull built for the air: a sleek white body with a full
// breast, layered grey-mantle wings with black wingtip finger feathers (the gull
// giveaway), a fanned white tail with a black band, a rounded head and the yellow
// beak. Two liveries: pale herring gull and a dark-mantled great black-backed
// gull. Ported 1:1 from age-of-mcp's v2/seagull.ts, using the library's shared
// `wildland` (eyes, wingPair) helpers.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { body: "#f7f9fb", inner: "#d3dade", outer: "#b6c1c9", tip: "#262b31", beak: "#f2b705" },
  { body: "#f2f5f7", inner: "#8b97a1", outer: "#4e5a64", tip: "#1d2227", beak: "#e8c33a" },
];

function seagullParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // sleek body + full breast
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.body,
      position: [0.005, 0.032, 0], scale: [1.7, 0.95, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.body,
      position: [0.035, 0.026, 0], scale: [1.05, 0.95, 0.9] },
    // head + yellow beak
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.body,
      position: [0.062, 0.052, 0] },
    { geometry: new THREE.ConeGeometry(0.0065, 0.024, 4), color: c.beak,
      position: [0.085, 0.049, 0], rotation: [0, 0, -Math.PI / 2] },
    // fanned tail with a black band
    { geometry: new THREE.ConeGeometry(0.014, 0.05, 4), color: c.body,
      position: [-0.07, 0.034, 0], rotation: [0, 0, Math.PI / 2],
      scale: [1, 1, 0.45] },
    { geometry: new THREE.BoxGeometry(0.008, 0.004, 0.026), color: c.tip,
      position: [-0.092, 0.035, 0] },
  ];
  parts.push(...wildland.eyes(0.068, 0.057, 0.012, 0.0035, "#181c20"));
  parts.push(...wildland.wingPair({
    root: [0, 0.04, 0.018], span: 0.155, chord: 0.05, rise: 0.024,
    sweep: 0.02, thick: 0.006,
    inner: c.inner, outer: c.outer, tip: c.tip, fingers: 2,
  }));
  return parts;
}

export const seagullV2 = defineModel({
  id: "seagull-v2",
  batchId: "fable",
  name: "Seagull (v2)",
  tags: ["creature","seagull","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly seagull.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.09,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => seagullParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => seagullParts(1) },
  ],
  animations: [],
});
